<template>
  <div>
    <!-- 超管首页 -->
    <SuperAdminDashboard v-if="isSuperAdmin" />
    
    <!-- 普通用户首页 -->
    <div v-else>
      <!-- 订阅已过期/暂停警告 -->
      <a-alert
        v-if="isSubscriptionExpired"
        type="warning"
        show-icon
        banner
        style="margin-bottom: 24px;"
      >
        <template slot="message">
          <span>您的订阅已暂停，部分功能受限。请联系管理员续费以恢复完整功能。</span>
        </template>
      </a-alert>

      <!-- 订阅到期警告 -->
      <a-alert
        v-if="showSubscriptionWarning"
        type="warning"
        show-icon
        closable
        :after-close="handleWarningClose"
        style="margin-bottom: 24px;"
      >
        <template slot="message">
          <span>您的订阅将于 <strong>{{ subscriptionExpiredAt }}</strong> 到期，剩余 <strong>{{ daysRemaining }}</strong> 天，请及时联系管理员续费。</span>
        </template>
      </a-alert>
      
      <!-- Counter Widgets -->
      <a-row :gutter="24">
        <a-col
          :span="24"
          :lg="12"
          :xl="6"
          class="mb-24"
          v-for="(stat, index) in stats"
          :key="index"
        >
          <!-- Widget Card -->
          <WidgetCounter
            :title="stat.title"
            :value="stat.value"
            :prefix="stat.prefix"
            :suffix="stat.suffix"
            :icon="stat.icon"
            :status="stat.status"
            :badge="stat.badge"
            :badgeStatus="stat.badgeStatus"
          ></WidgetCounter>
          <!-- / Widget Card -->
        </a-col>
      </a-row>
      <!-- / Counter Widgets -->

      <!-- Charts -->
      <a-row :gutter="24" type="flex" align="stretch">
        <a-col :span="24" :lg="10" class="mb-24">
          <!-- Active Users Card -->
          <CardBarChart v-if="[1,2].includes(info.role_id)"></CardBarChart>
          <!-- Active Users Card -->
        </a-col>
        <a-col :span="24" :lg="14" class="mb-24">
          <!-- Sales Overview Card -->
          <CardLineChart v-if="[1,2].includes(info.role_id)"></CardLineChart>
          <!-- / Sales Overview Card -->
        </a-col>
      </a-row>

      <!-- 事件日历 -->
      <a-card title="事件日历" class="calendar-card" :headStyle="headStyleCalendar">
        <a-spin :spinning="loading" tip="日历数据加载中...">
          <a-calendar :mode="calender_view_type" v-if="!loading" @panelChange="handlePanelChange">
            <ul slot="dateCellRender" slot-scope="value" class="events">
              <li
                v-for="item in getListData(value)"
                :key="item.content"
                @click="show_select_record(value, item)"
                class="event-item"
                :class="'event-' + item.type"
              >
                <span class="event-dot"></span>
                <span class="event-text">{{ item.content }}</span>
              </li>
            </ul>
            <ul slot="monthCellRender" slot-scope="value" class="events">
              <li
                v-for="item in getMonthListData(value)"
                :key="item.content"
                class="event-item"
                :class="'event-' + item.type"
              >
                <span class="event-dot"></span>
                <span class="event-text">{{ item.content }}</span>
              </li>
            </ul>
          </a-calendar>
        </a-spin>
      </a-card>

      <a-modal
        :visible="modalVisible"
        :title="modalTitle"
        @cancel="closeSelectRecordsModal"
        :footer="null"
        width="800px"
      >
        <div v-if="selectedRecords.length > 0">
          <select-record-row
            v-for="record in selectedRecords"
            :key="record.id"
            :record="record"
            :show-date-separator="false"
            @open-cell="open_cell(record.id, 'selectRecord')"
            :showDelete="false"
          ></select-record-row>
        </div>
        <a-empty v-else description="暂无选配记录" />
      </a-modal>
    </div>
  </div>
</template>

<script>
import CardBarChart from "../components/Cards/CardBarChart";
import CardLineChart from "../components/Cards/CardLineChart";
import WidgetCounter from "../components/Widgets/WidgetCounter";
import selectRecordRow from "./farms/components/selectRecordRow.vue";
import SuperAdminDashboard from './SuperAdminDashboard.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    CardBarChart,
    CardLineChart,
    WidgetCounter,
    selectRecordRow,
    SuperAdminDashboard
  },
  data() {
    return {
      headStyleCalendar: {
        backgroundColor: "#FFFFFF",
        color: "#111827",
        "border-radius": "16px 16px 0 0",
        "font-weight": "600",
        "font-size": "16px",
        "border-bottom": "none",
        "font-family": "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      },
      // Counter Widgets Stats
      stats: [],
      // 图标定义
      statIcons: {
        matching: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="currentColor"/>
        </svg>`,
        group: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
        </svg>`,
        birth: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
        </svg>`,
        death: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z" fill="currentColor"/>
        </svg>`
      },
      calender_data: {},
      loading: true, // 加载状态
			modalVisible: false, // Modal 是否可见
			modalTitle: "", // Modal 标题
			selectedRecords: [], // 当天的 selectRecord 记录
      calender_view_type: 'month',
      calender_query: {
        date: null
      },
      selectRecordVisible: false,
      records_query: {
        start_at: undefined,
        end_at: undefined,
        page_no: 1,
        page_size: 20
      },
      // 订阅到期警告相关
      subscriptionExpiredAt: null,
      daysRemaining: null,
      warningDismissed: false
    };
  },
  async created() {
    // 只有非超管用户才加载原有的统计数据
    if (!this.isSuperAdmin) {
      // 获取租户详情，检查订阅到期时间
      await this.checkSubscriptionExpiry();
      
      // 获取顶部统计数据
      let top_res = await this.$api.get_top_statistics();
      const data = top_res.data;

      // 格式化对比差值显示
      const formatComparison = (value) => {
        if (value > 0) return `+${value}`;
        if (value < 0) return `${value}`;
        return '0';
      };

      // 格式化百分比显示
      const formatPercentage = (value) => {
        if (value > 0) return `+${value}%`;
        if (value < 0) return `${value}%`;
        return '0%';
      };

      this.stats = [
        {
          title: "今日选配数量",
          value: data.today_select_item_count,
          icon: this.statIcons.matching,
          status: "primary",
          badge: `较昨日 ${formatComparison(data.select_comparison)}`,
          badgeStatus: data.select_comparison > 0 ? "success" : (data.select_comparison < 0 ? "danger" : "neutral"),
        },
        {
          title: "个体总数",
          value: data.total_item_count,
          icon: this.statIcons.group,
          status: "primary",
          badge: `存栏数量 ${data.inventory_count}`,
          badgeStatus: "neutral",
        },
        {
          title: "今日出生个体数",
          value: data.today_birth_item_count,
          icon: this.statIcons.birth,
          status: "primary",
          badge: `较昨日 ${formatComparison(data.birth_comparison)}`,
          badgeStatus: data.birth_comparison > 0 ? "success" : (data.birth_comparison < 0 ? "danger" : "neutral"),
        },
        {
          title: "近7天死亡个体数",
          value: data.last_7_days_dead_item_count,
          icon: this.statIcons.death,
          status: data.dead_comparison_percentage > 0 ? "danger" : "primary",
          badge: `环比 ${formatPercentage(data.dead_comparison_percentage)}`,
          badgeStatus: data.dead_comparison_percentage > 0 ? "danger" : "success",
        }
      ];
  
      // 获取日历数据
      this.calender_data = await this.$api.get_calender(this.calender_query);
    }
    this.loading = false; // 数据加载完成，关闭加载动画
  },
  computed: {
    ...mapGetters('main/user', [ 'info' ]),
    isSuperAdmin() {
      // 根据用户角色判断是否为超管
      // 超管: role.code === 100
      // 租户管理员: role.code === 200
      // 普通用户: role.code === 300
      return this.info?.role?.code === 100;
    },
    isSubscriptionExpired() {
      return this.info?.subscription?.subscriptionExpired === true;
    },
    showSubscriptionWarning() {
      // 显示条件：有到期时间、剩余天数<=30且>0、未被用户关闭、且订阅未完全过期
      return !this.isSubscriptionExpired &&
             this.subscriptionExpiredAt && 
             this.daysRemaining !== null && 
             this.daysRemaining > 0 && 
             this.daysRemaining <= 30 && 
             !this.warningDismissed;
    }
  },
  methods: {
    // 检查订阅到期时间
    async checkSubscriptionExpiry() {
      try {
        const tenantId = this.info?.tenant_id;
        if (!tenantId) return;
        
        const response = await this.$api.get_tenant_detail(tenantId);
        if (response && response.code === 200 && response.data && response.data.subscription_expired_at) {
          const expiredAt = new Date(response.data.subscription_expired_at);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          expiredAt.setHours(0, 0, 0, 0);
          
          // 计算剩余天数
          const diffTime = expiredAt.getTime() - today.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          // 格式化到期日期为 YYYY-MM-DD
          const year = expiredAt.getFullYear();
          const month = String(expiredAt.getMonth() + 1).padStart(2, '0');
          const day = String(expiredAt.getDate()).padStart(2, '0');
          
          this.subscriptionExpiredAt = `${year}-${month}-${day}`;
          this.daysRemaining = diffDays;
        }
      } catch (error) {
        console.error('获取租户详情失败:', error);
      }
    },
    
    // 用户关闭警告
    handleWarningClose() {
      this.warningDismissed = true;
    },
    
    getMonthListData(value) {
      
      const month = value.format('yyyy-MM'); // 获取当前月份（1-12）

      // 查找选配记录
      const selectRecord = this.calender_data.data.select_record_month_count.find(
        (item) => item.month === month
      );

      // 查找出生和死亡记录
      const birthDeadRecord = this.calender_data.data.birth_and_dead_item_month_count.find(
        (item) => item.month === month
      );

      const result = [];

      // 添加选配记录
      if (selectRecord && parseInt(selectRecord.count) > 0) {
        result.push({
          type: 'processing',
          content: `选配数量: ${selectRecord.count}次`,
        });
      }

      // 添加出生记录
      if (birthDeadRecord && parseInt(birthDeadRecord.birth_count) > 0) {
        result.push({
          type: 'success',
          content: `出生个体: ${birthDeadRecord.birth_count}只`,
        });
      }

      // 添加死亡记录
      if (birthDeadRecord && parseInt(birthDeadRecord.dead_count) > 0) {
        result.push({
          type: 'error',
          content: `死亡个体 ${birthDeadRecord.dead_count}只`,
        });
      }
      return result;
    },
    getListData(value) {
      const day = value.format('YYYY-MM-DD'); // 获取当前日期的天数（1-31）

      // 查找选配记录
      const selectRecord = this.calender_data.data.select_record_day_count.find(
        (item) => item.date === day
      );

      // 查找出生和死亡记录
      const birthDeadRecord = this.calender_data.data.birth_and_dead_item_count.find(
        (item) => item.date === day
      );

      const result = [];

      // 添加选配记录
      if (selectRecord && parseInt(selectRecord.count) > 0) {
        result.push({
          type: 'processing',
          content: `选配数量: ${selectRecord.count}次`,
          content_type: 'selectRecord',
        });
      }

      // 添加出生记录
      if (birthDeadRecord && parseInt(birthDeadRecord.birth_count) > 0) {
        result.push({
          type: 'success',
          content: `出生个体: ${birthDeadRecord.birth_count}只`,
          content_type: 'birthRecord',
        });
      }

      // 添加死亡记录
      if (birthDeadRecord && parseInt(birthDeadRecord.dead_count) > 0) {
        result.push({
          type: 'error',
          content: `死亡个体 ${birthDeadRecord.dead_count}只`,
          content_type: 'deadRecord',
        });
      }
      return result;
    },
    async show_select_record(date, item) {
      
      if (item.content_type === 'selectRecord') {
        // 获取当天的 selectRecord 记录
        const payload = {
          start_at: date.format('YYYY-MM-DD 00:00:00'),
          end_at: date.format('YYYY-MM-DD 23:59:59'),
        };
        
        const record_res = await this.$api.get_item_select_records(payload)
        this.selectedRecords = record_res.data;

        // 设置 Modal 标题和内容
        this.modalTitle = `选配记录 - ${date.format('YYYY-MM-DD')}`;

        // 显示 Modal
        this.modalVisible = true;
      }
    },
    async handlePanelChange(value) {
      this.calender_query.date = value;
      this.calender_data = await this.$api.get_calender(this.calender_query); 
    },
    closeSelectRecordsModal() {
      this.modalVisible = false;
    },
    open_cell(id, group_type='cell') {
      this.$router.push({
        path: `/farms/cell/${id}?group_type=${group_type}`
      })
    },
    deleteRecord() {}
  },
};
</script>

<style lang="scss">
// 事件日历样式
.calendar-card {
  border-radius: 16px;
  border: 1px solid #E5E7EB;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.1) 0px 1px 2px -1px;
  background: #FFFFFF;

  ::v-deep .ant-card-body {
    padding: 24px;
  }

  ::v-deep .ant-card-head {
    border-bottom: none;
    padding: 0 24px;
  }

  ::v-deep .ant-card-head-title {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    padding: 20px 0;
  }

  ::v-deep .ant-picker-calendar {
    .ant-picker-calendar-header {
      padding: 0 0 16px;
      border-bottom: none;
      margin-bottom: 16px;
    }

    .ant-picker-cell {
      padding: 4px 0;
    }

    .ant-picker-cell-inner {
      min-height: 80px;
    }
    
    // 日期数字样式
    .ant-picker-calendar-date-value {
      color: #111827;
      font-weight: 500;
      font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    
    // 今日日期样式
    .ant-picker-calendar-date-today {
      .ant-picker-calendar-date-value {
        color: #2563EB;
      }
    }
    
    // 选中日期样式
    .ant-picker-calendar-date-selected {
      .ant-picker-calendar-date-value {
        color: #FFFFFF;
      }
    }
    
    // 日期单元格hover效果
    .ant-picker-calendar-date:hover {
      background: #F9FAFB;
    }
  }
}

.events {
  list-style: none;
  margin: 0;
  padding: 0;

  .event-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    margin: 2px 0;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
    font-size: 12px;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;

    &:hover {
      background-color: #F9FAFB;
    }

    .event-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .event-text {
      color: #6B7280;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    // 事件类型颜色
    &.event-processing {
      .event-dot {
        background-color: #2563EB;
      }
    }

    &.event-success {
      .event-dot {
        background-color: #10B981;
      }
    }

    &.event-error {
      .event-dot {
        background-color: #EF4444;
      }
      .event-text {
        color: #EF4444;
      }
    }
  }
}

// 订阅警告样式优化
::v-deep .ant-alert-warning {
  border-radius: 12px;
  border: 1px solid #FCD34D;
  background: #FFFBEB;
  box-shadow: none;
}
</style>